/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { makeStyles } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import Slider from 'react-slick';

import { func, object, string } from 'prop-types';
import { useUIDSeed } from 'react-uid';
import OptionButton from './OptionButton';
import { getSSRDeviceType } from '../../../../../../../state/ducks/App/App-Selectors';

const useStyles = makeStyles((theme) => ({

    carouselContainer: {
        marginBottom: '25px',
        width: '100%',
    },

    carouselTitle: {
        color: 'gray',
        fontSize: '0.9em',
        marginLeft: '5px',
        marginBottom: '10px',
    },

    optionContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '0 22px',

        width: '100%',

        '&--flex-start': {
            justifyContent: 'flex-start',
        },
        '&--space-evenly': {
            justifyContent: 'space-evenly',
        },
        '&--space-around': {
            justifyContent: 'space-around',
        },

    },

    sliderContainer: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        '& .slick-track': {
            display: ' flex',
        },
        '& .slick-list': {
            overflow: 'inherit',
            margin: '0 0px',
        },
        '& .slick-slide': {
            display: 'inline-block',
            padding: '0 15px',
            position: 'relative',
            width: '0px',
        },
        '& .slick-prev, & .slick-next': {
            transform: 'translate(0,-50%)',
            cursor: 'pointer',
            border: 'none',
            outline: 0,
            fontSize: 0,
            lineHeight: 0,
            top: '50%',
            width: 16,
            height: 15,
            position: 'absolute',
            background: 'transparent',
        },
        '& .slick-prev': {
            left: '-4px',
        },
        '& .slick-next': {
            right: '-4px',
        },
        '& .slick-dots': {
            listStyle: 'none',
            display: 'flex !important',
            padding: '0',
            justifyContent: 'center',
            margin: '0',
            '& li': {
                lineHeight: '1',
            },
            '& .slick-active': {
                '& div': {
                    background: theme.palette.cms?.sliderNavDotSelectedBackground || 'rgb(47, 47, 47)',
                },
            },
            '& .slick-current': {
                '& div': {
                    paddingRight: '5px',
                },
            },
        },
        [theme.breakpoints.up(1000)]: {
            padding: '0 70px',
            '& .slick-prev, & .slick-next': {
                width: 25,
            },
        },
        [theme.breakpoints.up(600)]: {
            padding: '0 13px',
            '& .slick-prev, & .slick-next': {
                width: 25,
            },
        },
        [theme.breakpoints.down('xs')]: {
            padding: '0 30px',
            '& .slick-prev, & .slick-next': {
                width: 25,
            },
        },
    },
    slides: {
        '& .slick-list': {
            overflow: 'hidden',
            margin: '0 15px !important',
        },
    },
    slideItem: {
        height: '100%', // full height based on parent
        width: '100%',
        '& > div.rl-box': {
            position: 'inherit',
        },
    },
}));

const OptionCarousel = (props) => {
    const { data, localStateDispatcher, ssrDeviceType } = props;
    const {
        title,
        id,
        options,
        selected,
        canDeselect,
    } = data;

    const classes = useStyles();
    const seed = useUIDSeed();

    // 1. need the final list of options, pull out the hidden options
    const optionsToDisplay = options.filter((option) => !option.hidden);
    if (!optionsToDisplay.length) {
        return null;
    }

    let maxOptionsToShowAtOnce = 3;
    if (ssrDeviceType === 'mobile') {
        maxOptionsToShowAtOnce = 2;
    }

    let containerJustifyContent;
    if (optionsToDisplay.length === 1) {
        containerJustifyContent = '--flex-start';
    } else if (optionsToDisplay.length === 2) {
        containerJustifyContent = '--space-evenly';
    } else if (optionsToDisplay.length === 3) {
        containerJustifyContent = '--space-around';
    }

    let buttonStyleOverride;
    if (optionsToDisplay.length < maxOptionsToShowAtOnce) {
        buttonStyleOverride = {
            width: '50%', // defaults to 30%
            margin: '0 5px',
        };
    }

    if (optionsToDisplay.length === maxOptionsToShowAtOnce) {
        buttonStyleOverride = {
            width: '178px', // defaults to 30%
            margin: '0 5px',
        };
    }

    // 2. map over the options and produce JSX with style overrides given the total number of options to display
    const Options = optionsToDisplay.map((option) => {
        const { value, enabled, cmsInfo } = option;

        const isSelected = selected.includes(value);

        let displayValue = value;
        let thumbnailLink = '';
        let hardcodedPrice = null;
        if (cmsInfo) {
            displayValue = cmsInfo.displayValue;
            thumbnailLink = cmsInfo.image;
            hardcodedPrice = cmsInfo.hardcodedPrice;
        }

        if (displayValue === value) {
            displayValue = value
                .split('_')
                .map((word) => {
                    const wordArr = word.split('');
                    wordArr[0] = wordArr[0].toUpperCase();
                    return wordArr.join('');
                })
                .join(' ');
        }

        return (
            <OptionButton
                key={value}
                styleOverride={buttonStyleOverride}
                title={displayValue}
                thumbnailLink={thumbnailLink}
                hardcodedPrice={hardcodedPrice}
                selected={isSelected}
                enabled={enabled}
                handleOnClick={() => {
                    if (!isSelected) {
                        localStateDispatcher('select', { id, value });
                        return;
                    }

                    if (isSelected && canDeselect) {
                        localStateDispatcher('deselect', { id, value });
                    }
                }}
            />
        );
    });

    if (Options.length > maxOptionsToShowAtOnce) {
        return  (
            <div className={classes.carouselContainer}>
                <div className={classes.carouselTitle}>
                    {title}
                </div>
                <Slider
                    dots={false}
                    infinite
                    speed={500}
                    slidesToShow={maxOptionsToShowAtOnce}
                    slidesToScroll={1}
                    className={`carousel-container ${classes.sliderContainer}`}
                    nextArrow={<ArrowForwardIosIcon />}
                    prevArrow={<ArrowBackIosIcon />}
                >
                    {Options.map((Slide) => (
                        <div
                            className={classes.slideItem}
                            key={seed(Slide)}
                        >
                            {Slide}
                        </div>
                    ))}
                </Slider>
            </div>
        );
    }

    return (
        <div className={classes.carouselContainer}>
            <div className={classes.carouselTitle}>
                {title}
            </div>
            <div className={`${classes.optionContainer} ${classes.optionContainer}${containerJustifyContent}`}>
                {Options}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    ssrDeviceType: getSSRDeviceType(state),
});

OptionCarousel.propTypes = {
    ssrDeviceType: string.isRequired,
    data: object.isRequired,
    localStateDispatcher: func.isRequired,
};

const enhance = compose(
    connect(mapStateToProps),
);

export default enhance(OptionCarousel);
